import { DialogTrigger } from '@radix-ui/react-dialog';
import { useReactTable } from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';
import { match } from 'ts-pattern';
import { z } from 'zod';

import { useApiClient } from '@eluve/api-client-provider';
import { AppointmentExportTypesEnum, ExportEnums } from '@eluve/api-contract';
import { QUERY_ROOT_ID, useCompleteFragment } from '@eluve/apollo-client';
import {
  AppointmentTag,
  AppointmentTagData,
  ComputedAppointmentStatusLabel,
  TooltipLabel,
} from '@eluve/blocks';
import {
  ColDefBuilder,
  DataTableFilterField,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ExtendedSortingState,
  HStack,
  NewButton,
  PageTitle,
  Paragraph,
  ServerDataTable,
  Tag,
  Textarea,
  useDataTableForQuery,
  useToast,
} from '@eluve/components';
import {
  AppointmentsBoolExp,
  AppointmentsOrderBy,
  DbEnums,
} from '@eluve/graphql-types';
import { ResultOf } from '@eluve/graphql.tada';
import { formatAppointmentTagLabel } from '@eluve/utils';

import {
  reviewerTenantsFragment,
  reviewerUsersFragment,
  searchAppointmentsQuery,
} from './operations';
import { useLowQualityFactors } from './useLowQualityFactors';

type TableRow = {
  tenantId: string;
  tenantName: string;
  appointmentId: string;
  appointmentName: string;
  userId: string;
  userEmail: string;
  updatedAt: string;
  status: string;
  lowQualityFactors: string[];
  excludeLowQualityFactors: string[];
  tags: AppointmentTagData[];
  annotationCount: number;
  goldenNotesAndFactsCount: { notes: number; facts: number };
};

const columns = new ColDefBuilder<TableRow>()
  .detailsLink(
    (row) => `/review/appointments/${row.tenantId}/${row.appointmentId}`,
    'Appointment',
  )
  .defaultSortable('appointmentName', 'Name')
  .defaultSortable('tenantId', {
    label: 'Organization',
    cellRenderer: (row) => row.tenantName,
  })
  .defaultSortable('userId', {
    label: 'User',
    cellRenderer: (row) => row.userEmail,
  })
  .dateSortable('updatedAt', 'Updated At')
  .defaultSortable('status', {
    cellRenderer: ({ status }) => {
      return <ComputedAppointmentStatusLabel statusLabel={status} />;
    },
  })
  .defaultSortable('annotationCount', '# Annotations')
  .colDef({
    id: 'tags',
    header: 'Tags',
    cell: ({ row }) => {
      return (
        <HStack>
          {row.original.tags.map((t) => (
            <AppointmentTag key={t.id} tag={t} size="xs" />
          ))}
        </HStack>
      );
    },
  })
  .colDef({
    id: 'lowQualityFactors',
    header: 'Low Quality Factors',
    cell: ({ row }) => {
      return (
        <HStack>
          {row.original.lowQualityFactors.map((lqf) => (
            <Tag
              color="negative"
              size="xs"
              pill
              key={lqf}
              label={formatAppointmentTagLabel(lqf)}
            />
          ))}
        </HStack>
      );
    },
  })
  .colDef({
    id: 'excludeLowQualityFactors',
    header: '',
    cell: () => {
      return <HStack></HStack>;
    },
  })
  .colDef({
    id: 'goldenNotesAndFactsCount',
    header: 'Golden Notes & Facts',
    cell: ({ row }) => {
      const { notes, facts } = row.original.goldenNotesAndFactsCount;
      return (
        <HStack>
          <Tag color="teal" size="xs" pill label={`Notes: ${notes}`} />
          <Tag color="teal" size="xs" pill label={`Facts: ${facts}`} />
        </HStack>
      );
    },
  })
  .build();

const convertToRows = (results: ResultOf<typeof searchAppointmentsQuery>) => {
  return (
    (results?.appointments ?? []).map<TableRow>((a) => {
      return {
        tenantId: a.tenant.id,
        tenantName: a.tenant.name,
        appointmentId: a.id,
        appointmentName: a.name,
        userId: a.user?.id ?? '',
        userEmail: a.user?.email ?? '',
        updatedAt: a.updatedAt,
        status: a.status ?? '',
        lowQualityFactors: a.low_quality_factors.map((lqf) => lqf.factor),
        annotationCount: a.annotationsAggregate?.aggregate?.count ?? 0,
        tags: a.tags.map((t) => t.tag),
        excludeLowQualityFactors: [],
        goldenNotesAndFactsCount: {
          notes: a.completedGoldenNotes?.aggregate?.count ?? 0,
          facts: a.verifiedGoldenFacts?.aggregate?.count ?? 0,
        },
      };
    }) ?? []
  );
};

const defaultSort: ExtendedSortingState<TableRow> = [
  {
    id: 'updatedAt',
    desc: true,
  },
];

const uuidSchema = z.string().uuid();

export const AdminExportAppointmentsPage: React.FC = () => {
  const { tenants } = useCompleteFragment({
    fragment: reviewerTenantsFragment,
    key: QUERY_ROOT_ID,
  });
  const toast = useToast();

  const { users } = useCompleteFragment({
    fragment: reviewerUsersFragment,
    key: QUERY_ROOT_ID,
  });

  const [selectedTenants, setSelectedTenants] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedAppointmentTypes, setSelectedAppointmentTypes] = useState<
    AppointmentExportTypesEnum[]
  >([]);
  const [selectedLowQualityFactors, setSelectedLowQualityFactors] = useState<
    string[]
  >([]);
  const [
    selectedExcludeLowQualityFactors,
    setSelectedExcludeLowQualityFactors,
  ] = useState<string[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [textareaContent, setTextareaContent] = useState('');
  const [isExportConfirmationOpen, setIsExportConfirmationOpen] =
    useState(false);
  const [pendingExportAction, setPendingExportAction] = useState<
    'bucket' | 'byId' | null
  >(null);

  const lowQualityFactors = useLowQualityFactors();

  const filterFields: DataTableFilterField<TableRow>[] = useMemo(() => {
    const fields: DataTableFilterField<TableRow>[] = [
      {
        id: 'tenantId',
        label: 'Organization',
        options: tenants.map((t) => ({
          label: t.name,
          value: t.id,
        })),
      },
      {
        id: 'userId',
        label: 'User',
        options: users.map((u) => ({
          label: u.email,
          value: u.id,
        })),
      },
      {
        id: 'status',
        label: 'Type of Appointment',
        options: [
          {
            label: 'Has Golden Transcript',
            value: ExportEnums.AppointmentExportTypes.HAS_GOLDEN_TRANSCRIPT,
          },
          {
            label: 'Has Golden Summary',
            value: ExportEnums.AppointmentExportTypes.HAS_GOLDEN_SUMMARY,
          },
          {
            label: 'Has Golden Facts',
            value: ExportEnums.AppointmentExportTypes.HAS_GOLDEN_FACTS,
          },
        ],
      },
      {
        id: 'lowQualityFactors',
        label: 'Include Low Quality Factors',
        options: lowQualityFactors.map((lqf) => ({
          label: lqf.name.replaceAll('_', ' '),
          value: lqf.name,
        })),
      },
      {
        id: 'excludeLowQualityFactors',
        label: 'Exclude Low Quality Factors',
        options: lowQualityFactors.map((lqf) => ({
          label: lqf.name.replaceAll('_', ' '),
          value: lqf.name,
        })),
      },
    ];

    return fields;
  }, [tenants, users, lowQualityFactors]);

  const { reactTableOptions, isPending, data, rows } = useDataTableForQuery({
    query: searchAppointmentsQuery,
    convertToRows,
    filterFields,
    defaultSort,
    convertSearchParamsToVariables: ({ limit, offset, sorting, filters }) => {
      const filterConditions: AppointmentsBoolExp[] = [];

      if (filters.status && filters.status.length) {
        if (filters.status.includes('HAS_GOLDEN_TRANSCRIPT')) {
          filterConditions.push(
            {
              tags: {
                tag: {
                  tag: {
                    _eq: 'GOLDEN_TRANSCRIPT',
                  },
                },
              },
            },
            {
              status: {
                _eq: DbEnums.AppointmentStatus.COMPLETED,
              },
            },
            {
              transcripts: {
                transcriptType: {
                  _eq: DbEnums.TranscriptTypes.BATCH,
                },
              },
            },
          );
        }
        if (filters.status.includes('HAS_GOLDEN_SUMMARY')) {
          filterConditions.push({
            appointment_golden_notesAggregate: {
              count: {
                predicate: { _gt: 0 },
                filter: { status: { _eq: 'COMPLETED' } },
              },
            },
          });
        }
        if (filters.status.includes('HAS_GOLDEN_FACTS')) {
          filterConditions.push({
            appointment_golden_factsAggregate: {
              count: {
                predicate: { _gt: 0 },
                filter: { isVerified: { _eq: true } },
              },
            },
          });
        }
        setSelectedAppointmentTypes(
          filters.status as AppointmentExportTypesEnum[],
        );
      } else {
        setSelectedAppointmentTypes([]);
      }
      if (filters.tenantId && filters.tenantId.length) {
        filterConditions.push({
          tenantId: {
            _in: filters.tenantId as string[],
          },
        });
        setSelectedTenants(filters.tenantId as string[]);
      } else {
        setSelectedTenants([]);
      }

      if (filters.userId && filters.userId.length) {
        filterConditions.push({
          userId: {
            _in: filters.userId as string[],
          },
        });
        setSelectedUsers(filters.userId as string[]);
      } else {
        setSelectedUsers([]);
      }

      filterConditions.push();

      if (filters.lowQualityFactors && filters.lowQualityFactors.length) {
        filterConditions.push({
          low_quality_factors: {
            factor: {
              _in: filters.lowQualityFactors as string[],
            },
          },
        });
        setSelectedLowQualityFactors(filters.lowQualityFactors as string[]);
      } else {
        setSelectedLowQualityFactors([]);
      }

      if (
        filters.excludeLowQualityFactors &&
        filters.excludeLowQualityFactors.length
      ) {
        filterConditions.push({
          _not: {
            low_quality_factors: {
              factor: { _in: filters.excludeLowQualityFactors as string[] },
            },
          },
        });
        setSelectedExcludeLowQualityFactors(
          filters.excludeLowQualityFactors as string[],
        );
      } else {
        setSelectedExcludeLowQualityFactors([]);
      }

      return {
        filter: {
          _and: filterConditions,
        },
        offset,
        limit,
        orderBy:
          sorting && sorting.length
            ? sorting.map(({ desc, id }) => ({
                ...match<keyof TableRow, AppointmentsOrderBy>(id)
                  .with('userId', () => ({
                    user: {
                      email: desc ? 'DESC' : 'ASC',
                    },
                  }))
                  .with('appointmentName', () => ({
                    name: desc ? 'DESC' : 'ASC',
                  }))
                  .with('tenantId', () => ({
                    tenant: {
                      name: desc ? 'DESC' : 'ASC',
                    },
                  }))
                  .with('annotationCount', () => ({
                    annotationsAggregate: {
                      count: desc ? 'DESC' : 'ASC',
                    },
                  }))
                  .otherwise(() => ({
                    [id]: desc ? 'DESC' : 'ASC',
                  })),
              }))
            : null,
      };
    },
  });

  const rowCount = data?.appointmentsAggregate?.aggregate?.count ?? 0;

  const table = useReactTable({
    data: rows,
    columns,
    rowCount,
    ...reactTableOptions,
  });
  const apiClient = useApiClient();

  const showExportConfirmation = () => {
    setPendingExportAction('bucket');
    setIsExportConfirmationOpen(true);
  };

  const showExportByIdConfirmation = () => {
    if (selectedTenants.length !== 1 || !selectedTenants[0]) {
      return;
    }
    const lines = textareaContent
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    const validationResults = lines.map((line) => {
      const result = uuidSchema.safeParse(line);
      return { line, isValid: result.success };
    });

    const invalidLines = validationResults.filter((result) => !result.isValid);

    if (invalidLines.length > 0) {
      toast.error(
        `Invalid UUIDs found: ${invalidLines.map((r) => r.line).join(', ')}`,
      );
      return;
    }

    setPendingExportAction('byId');
    setIsExportConfirmationOpen(true);
  };

  const handleConfirmedExport = async () => {
    setIsExportConfirmationOpen(false);
    if (pendingExportAction === 'bucket') {
      await handleExport();
    } else if (pendingExportAction === 'byId') {
      await handleExportByIds();
    }
    setPendingExportAction(null);
  };

  const handleCancelExport = () => {
    setIsExportConfirmationOpen(false);
    setPendingExportAction(null);
  };

  const handleExport = async () => {
    if (selectedTenants.length !== 1 || !selectedTenants[0]) {
      return;
    }
    setIsExporting(true);
    try {
      const response =
        await apiClient.adminTranscript.exportTranscriptArtifacts({
          body: {
            includeTags: selectedLowQualityFactors,
            excludeTags: selectedExcludeLowQualityFactors,
            tenantId: selectedTenants[0],
            userIds: selectedUsers,
            appointmentTypes: selectedAppointmentTypes,
          },
        });
      if (response.status === 201) {
        toast.success(`Exporting appointment artifacts`);
      }
    } catch {
      toast.error('Failed to start export.');
    } finally {
      setTimeout(() => {
        setIsExporting(false);
      }, 500);
    }
  };
  const handleExportByIds = async () => {
    if (selectedTenants.length !== 1 || !selectedTenants[0]) {
      return;
    }
    const lines = textareaContent
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    const uuidSchema = z.string().uuid();
    const validIds = lines.filter((line) => uuidSchema.safeParse(line).success);

    setIsExporting(true);
    try {
      const response =
        await apiClient.adminTranscript.exportTranscriptArtifactsByAppointmentIds(
          {
            body: {
              tenantId: selectedTenants[0],
              appointmentIds: validIds,
            },
          },
        );
      if (response.status === 201) {
        toast.success(`Exporting ${validIds.length} appointments`);
        setIsDialogOpen(false);
        setTextareaContent('');
      }
    } catch {
      toast.error('Failed to start export.');
    } finally {
      setTimeout(() => {
        setIsExporting(false);
      }, 500);
    }
  };
  return (
    <div>
      <HStack className="justify-between">
        <PageTitle>Appointments</PageTitle>
        <HStack>
          <TooltipLabel
            label={`${selectedTenants.length !== 1 ? 'Export only works when one organization is selected' : ''}`}
          >
            <NewButton
              text="Export to Bucket"
              icon={{ name: 'CloudUpload' }}
              onClick={showExportConfirmation}
              disabled={selectedTenants.length !== 1 || isExporting}
              loading={isExporting}
            />
          </TooltipLabel>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen} modal>
            <DialogTrigger asChild>
              <TooltipLabel
                label={`${selectedTenants.length !== 1 ? 'Export only works when one organization is selected' : ''}`}
              >
                <NewButton
                  text="Export By ID"
                  icon={{ name: 'CloudUpload' }}
                  disabled={selectedTenants.length !== 1 || isExporting}
                  loading={isExporting}
                  onClick={() => setIsDialogOpen(true)}
                />
              </TooltipLabel>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Export By ID</DialogTitle>
              </DialogHeader>
              <Paragraph>Enter appointment IDs (one per line):</Paragraph>
              <Textarea
                value={textareaContent}
                onChange={(e) => setTextareaContent(e.target.value)}
                placeholder="Enter appointment IDs here..."
              />
              <DialogFooter>
                <NewButton
                  text="Cancel"
                  type="outline"
                  onClick={() => setIsDialogOpen(false)}
                  disabled={isExporting}
                />
                <NewButton
                  text="Export"
                  onClick={showExportByIdConfirmation}
                  disabled={isExporting || !textareaContent.trim()}
                  loading={isExporting}
                />
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </HStack>
      </HStack>

      <Dialog
        open={isExportConfirmationOpen}
        onOpenChange={setIsExportConfirmationOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Export</DialogTitle>
          </DialogHeader>
          <Paragraph size="md" color="secondary">
            Are you sure you want to start the export? This action will begin
            the export process immediately.
          </Paragraph>
          <DialogFooter>
            <NewButton
              text="Cancel"
              type="outline"
              onClick={handleCancelExport}
              disabled={isExporting}
            />
            <NewButton
              text="Start Export"
              onClick={handleConfirmedExport}
              disabled={isExporting}
              loading={isExporting}
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ServerDataTable<TableRow>
        table={table}
        filterFields={filterFields}
        isPending={isPending}
      />
    </div>
  );
};
